import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePagination } from "@/lib/usePagination";
import { CURRENCIES, autoCurrency, localizedCurrencyLabel } from "@/lib/currencies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ShoppingBag, Plus, MapPin, Search, LayoutGrid, Package, BookOpen, Music, Shirt, Laptop, Wrench, MessageCircle, Loader2, Heart, Trash2, Pencil, Check, Tag, PackageCheck, ImagePlus, X, Star, Percent, PlusCircle, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MediaGrid, resolveMediaUrl } from "@/components/FileUpload";
import ImageCropUpload from "@/components/ImageCropUpload";
import { CardGridSkeleton } from "@/components/FeedSkeleton";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { avatarSrc } from "@/lib/defaults";
import { ListingCard } from "@/components/marketplace/ListingCard";
import { ListingPrice, PromoControls, PromoFields, usePromoRules, promoInputValid } from "@/components/marketplace/Promo";
import { ListingStats } from "@/components/marketplace/ListingStats";
import { SellerDashboard } from "@/components/marketplace/SellerDashboard"; // lot 701 — « Mes annonces »
import { ShareToStorySheet } from "@/components/ShareToStory";

const CATS = [
  { key: "all", labelKey: "market.cat_all", fb: "Tout", icon: LayoutGrid },
  { key: "other", labelKey: "market.cat_other", fb: "Divers", icon: Package },
  { key: "books", labelKey: "market.cat_books", fb: "Livres", icon: BookOpen },
  { key: "music", labelKey: "market.cat_music", fb: "Musique", icon: Music },
  { key: "clothing", labelKey: "market.cat_clothing", fb: "Vêtements", icon: Shirt },
  { key: "electronics", labelKey: "market.cat_electronics", fb: "Électronique", icon: Laptop },
  { key: "services", labelKey: "market.cat_services", fb: "Services", icon: Wrench },
];
const catLabel = (k, t) => { const c = CATS.find((x) => x.key === k); return c ? t(c.labelKey, c.fb) : k; };
const CREATE_CATS = CATS.filter((c) => c.key !== "all");

export const Marketplace = () => {
  const { openChat, t, user, config, lang } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultCurrency = useMemo(
    () => autoCurrency({ userCountry: user?.country, preferredCurrency: user?.preferred_currency, defaultCurrency: config?.settings?.default_currency }),
    [user, config]
  );
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [appliedPrice, setAppliedPrice] = useState({ min: "", max: "" });
  const [promoView, setPromoView] = useState(false); // lot 694 — section « Promos en cours »
  const promoRules = usePromoRules(); // lot 700 — interrupteur + règles admin (config publique)
  const [launchPromo, setLaunchPromo] = useState({ on: false, price: "", ends: "" }); // lot 700 — « Promo de lancement » dans « Vendre un article »
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [contactMsg, setContactMsg] = useState(t("market.contact_default", "Bonjour, votre article est-il toujours disponible ?"));
  const [sendingMsg] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", price: 10, currency: "EUR", images: [], category: "other", city: "" });
  // Lot 623 — photos max par annonce (Admin › Limites, défaut 5) ; photo principale = 1ʳᵉ de la liste.
  const maxImages = Math.max(1, Number(config?.settings?.marketplace_max_images) || 5);
  const setCover = (i) => setForm((f) => (i <= 0 ? f : { ...f, images: [f.images[i], ...f.images.filter((_, j) => j !== i)] }));
  const [activeImg, setActiveImg] = useState(0);
  useEffect(() => { setActiveImg(0); }, [detail?.id]);
  const [savingCover, setSavingCover] = useState(false);
  const saveCover = async (it, i) => {
    if (i <= 0 || savingCover) return;
    const images = [it.images[i], ...it.images.filter((_, j) => j !== i)];
    setSavingCover(true);
    try {
      const { data } = await api.patch(`/marketplace/listings/${it.id}`, { images });
      const imgs = data.listing.images;
      patchItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, images: imgs } : x)));
      setFavItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, images: imgs } : x)));
      setDetail((d) => (d ? { ...d, images: imgs } : d));
      setActiveImg(0);
      toast.success(t("market.cover_saved", "Photo principale mise à jour"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); } finally { setSavingCover(false); }
  };
  // Keep the create form's default currency in sync with the detected region while it's closed.
  useEffect(() => { if (!open) setForm((f) => ({ ...f, currency: defaultCurrency })); }, [defaultCurrency, open]);

  const fetcher = useCallback(async (skip, limit) => {
    const qs = new URLSearchParams({ skip, limit, sort });
    if (category !== "all") qs.set("category", category);
    if (appliedPrice.min !== "") qs.set("min_price", appliedPrice.min);
    if (appliedPrice.max !== "") qs.set("max_price", appliedPrice.max);
    if (promoView) qs.set("promo", "1"); // lot 694 — section « Promos en cours »
    const { data } = await api.get(`/marketplace/listings?${qs.toString()}`);
    return { items: data.listings || [], hasMore: data.has_more };
  }, [category, sort, appliedPrice, promoView]);
  const { items, patchItems, loading, loadingMore, hasMore, sentinelRef, reload } = usePagination(fetcher, { limit: 24, deps: [category, sort, appliedPrice, promoView] });


  const applyPrice = () => setAppliedPrice({ min: priceMin, max: priceMax });
  const clearFilters = () => { setPriceMin(""); setPriceMax(""); setAppliedPrice({ min: "", max: "" }); setSort("recent"); };
  const filtersActive = appliedPrice.min !== "" || appliedPrice.max !== "" || sort !== "recent";

  const [favView, setFavView] = useState(false);
  const [mineView, setMineView] = useState(false); // lot 701 — « Mes annonces » : tableau vues / favoris / messages du vendeur
  const [favItems, setFavItems] = useState([]);
  const [favLoading, setFavLoading] = useState(false);
  const [priceEdit, setPriceEdit] = useState(null);
  const [shareStory, setShareStory] = useState(null); // lot 697 — « Partager la promo en story » (vendeur, promo en cours)
  const [savingPrice, setSavingPrice] = useState(false);

  // Lot 694 — bandeau « Promos en cours » en tête de la liste générale (8 premières, fin la plus proche d'abord).
  const [promoStrip, setPromoStrip] = useState([]);
  useEffect(() => {
    if (!promoRules.enabled) { setPromoStrip([]); return; } // lot 700 — promos désactivées : pas de bandeau
    api.get("/marketplace/listings?promo=1&limit=8").then(({ data }) => setPromoStrip(data.listings || [])).catch(() => setPromoStrip([]));
  }, [promoView, promoRules.enabled]);
  // Une annonce modifiée (prix, promo, photos, vendu) est mise à jour partout : grille, favoris, bandeau, fiche ouverte.
  const patchListing = useCallback((updated) => {
    const merge = (x) => (x.id === updated.id ? { ...x, ...updated } : x);
    patchItems((prev) => prev.map(merge));
    setFavItems((prev) => prev.map(merge));
    setPromoStrip((prev) => {
      const next = prev.map(merge).filter((x) => x.promo);
      return !prev.some((x) => x.id === updated.id) && updated.promo && updated.active !== false ? [updated, ...next] : next;
    });
    setDetail((d) => (d && d.id === updated.id ? { ...d, ...updated } : d));
  }, [patchItems]);

  const loadFavorites = useCallback(async () => {
    setFavLoading(true);
    try { const { data } = await api.get("/marketplace/favorites"); setFavItems(data.listings || []); }
    catch { setFavItems([]); }
    setFavLoading(false);
  }, []);
  useEffect(() => { if (favView) loadFavorites(); }, [favView, loadFavorites]);

  const applyFav = (id, val) => {
    patchItems((prev) => prev.map((x) => (x.id === id ? { ...x, is_favorite: val } : x)));
    setFavItems((prev) => (val ? prev : prev.filter((x) => x.id !== id)));
    setDetail((d) => (d && d.id === id ? { ...d, is_favorite: val } : d));
  };
  const toggleFav = async (it, e) => {
    e?.stopPropagation?.();
    if (!user) { toast.error(t("market.fav_login", "Connecte-toi pour enregistrer des favoris")); return; }
    const next = !it.is_favorite;
    applyFav(it.id, next);
    try { await api.post(`/marketplace/listings/${it.id}/favorite`); }
    catch { applyFav(it.id, !next); toast.error(t("common.error", "Erreur")); }
  };
  const removeListing = async (it) => {
    if (!window.confirm(t("market.delete_confirm", "Supprimer définitivement cette annonce ?"))) return;
    try {
      await api.delete(`/marketplace/listings/${it.id}`);
      patchItems((prev) => prev.filter((x) => x.id !== it.id));
      setFavItems((prev) => prev.filter((x) => x.id !== it.id));
      setDetail(null);
      toast.success(t("market.deleted", "Annonce supprimée"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const savePrice = async (it) => {
    const p = parseFloat(priceEdit);
    if (isNaN(p) || p < 0) { toast.error(t("market.price_invalid", "Prix invalide")); return; }
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/marketplace/listings/${it.id}`, { price: p });
      const np = data.listing.price;
      patchListing(data.listing); // lot 694 — met aussi à jour la promo (retirée si le prix passe en dessous)
      setPriceEdit(null);
      toast.success(np < it.price ? t("market.price_lowered", "Prix baissé — les personnes l'ayant en favori sont prévenues 🔔") : t("market.price_updated", "Prix mis à jour"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setSavingPrice(false);
  };
  const isMine = (it) => it?.is_mine || (user && it?.seller?.id === user.id);

  // Lot 698 — chaque ouverture de fiche est signalée au serveur avec sa provenance (« marketplace » par défaut, « story »
  // quand on arrive par le bouton « Voir l'annonce » d'une story : /marketplace?item=…&from=story). Le serveur dédoublonne
  // (1 vue / membre / source / jour) et ignore le vendeur ; c'est ce qui alimente les compteurs du vendeur et le bilan de promo.
  const viewSourceRef = useRef("marketplace");
  useEffect(() => {
    if (!detail?.id || isMine(detail)) return;
    const source = viewSourceRef.current;
    viewSourceRef.current = "marketplace";
    api.post(`/marketplace/listings/${detail.id}/view`, { source }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  const toggleSold = async (it) => {
    const nextActive = it.active === false; // if currently sold → put back on sale
    try {
      const { data } = await api.patch(`/marketplace/listings/${it.id}`, { active: nextActive });
      const act = data.listing.active;
      patchListing(data.listing);
      toast.success(act ? t("market.back_on_sale", "Remise en vente — les favoris sont prévenus 🔔") : t("market.marked_sold", "Annonce marquée comme vendue"));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  // Open the real Messenger-style chat dock with the seller — the listing card travels
  // with the first message (WhatsApp Business-style) so the seller knows WHICH item it is.
  const contactSeller = () => {
    if (!detail?.seller) return;
    const seller = detail.seller;
    const ref = { type: "listing", id: detail.id, title: detail.title, price: detail.promo?.price ?? detail.price, currency: detail.currency, image_url: detail.images?.[0] || null, author_id: detail.seller_id };
    setDetail(null);
    openChat(seller, { ref, draft: (contactMsg || "").trim() || undefined });
  };

  // Deep-link /marketplace?item=<id> (from a chat context card) → open that listing.
  // Lot 695 — /marketplace?promo=1 (carte « Promo du jour » du fil) → section « Promos en cours ».
  useEffect(() => {
    const id = searchParams.get("item");
    const promo = searchParams.get("promo");
    if (!id && !promo) return;
    if (id) {
      viewSourceRef.current = searchParams.get("from") || "link"; // lot 698 — provenance de l'ouverture (story, chat…)
      api.get(`/marketplace/listings/${id}`).then(({ data }) => setDetail(data.listing)).catch(() => toast.error(t("common.error", "Erreur")));
    }
    if (promo === "1") { setPromoView(true); setFavView(false); }
    const sp = new URLSearchParams(searchParams);
    sp.delete("item"); sp.delete("promo"); sp.delete("from");
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    const src = favView ? favItems : items;
    return src.filter((it) => !term || it.title?.toLowerCase().includes(term) || it.description?.toLowerCase().includes(term));
  }, [items, favItems, favView, q]);

  const create = async () => {
    if (!form.title.trim()) return toast.error(t("market.err_title", "Titre requis"));
    // Lot 700 — promo de lancement : vérifiée localement (règles admin) avant l'envoi, puis par le serveur.
    let promo;
    if (launchPromo.on && promoRules.enabled) {
      if (!promoInputValid(form.price, launchPromo.price, launchPromo.ends, promoRules)) return toast.error(t("market.promo_invalid", "Indique un prix promo inférieur au prix et une date de fin à venir"));
      promo = { price: parseFloat(launchPromo.price), ends_at: new Date(`${launchPromo.ends}T23:59:59`).toISOString() };
    }
    setCreating(true);
    try {
      await api.post("/marketplace/listings", {
        title: form.title, description: form.description, price: Number(form.price),
        currency: form.currency, images: form.images, category: form.category, city: form.city,
        ...(promo ? { promo } : {}),
      });
      toast.success(promo ? t("market.published_promo", "Annonce publiée avec sa promo 🎉") : t("market.published", "Annonce publiée 🎉"));
      setOpen(false);
      setForm({ title: "", description: "", price: 10, currency: defaultCurrency, images: [], category: "other", city: "" });
      setLaunchPromo({ on: false, price: "", ends: "" });
      reload();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setCreating(false);
  };

  const SellButton = (
    <Button className="pill-primary rounded-full w-full" onClick={() => setOpen(true)} data-testid="marketplace-create-btn">
      <Plus size={16} className="mr-1" /> {t("market.sell", "Vendre un article")}
    </Button>
  );

  return (
    <AppShell showSidebars={false}>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6" data-testid="marketplace-page">
        {/* Left rail (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 space-y-4">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "#2D9E681f" }}><ShoppingBag size={18} color="#2D9E68" /></span>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Marketplace</h1>
            </div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
              <Input placeholder={t("market.search_ph", "Rechercher…")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full" data-testid="marketplace-search" />
            </div>
            {SellButton}
            <div className="yalola-card p-2">
              <div className="px-2 py-1 text-xs font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("market.categories", "Catégories")}</div>
              {CATS.map((c) => {
                const active = category === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
                    style={{ background: active ? "var(--yalola-primary-soft)" : "transparent" }}
                    data-testid={`marketplace-cat-${c.key}`}
                  >
                    <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: active ? "var(--yalola-primary)" : "var(--yalola-surface-alt)" }}>
                      <c.icon size={16} color={active ? "#fff" : "var(--yalola-text)"} />
                    </span>
                    <span className="text-sm" style={{ fontWeight: active ? 700 : 500, color: active ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{t(c.labelKey, c.fb)}</span>
                  </button>
                );
              })}
            </div>
            <div className="yalola-card p-2" data-testid="marketplace-fav-toggle-card">
              <button
                onClick={() => { setFavView((v) => !v); setMineView(false); }}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
                style={{ background: favView ? "var(--yalola-primary-soft)" : "transparent" }}
                data-testid="marketplace-fav-toggle"
              >
                <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: favView ? "var(--yalola-primary)" : "var(--yalola-surface-alt)" }}>
                  <Heart size={16} color={favView ? "#fff" : "var(--yalola-text)"} fill={favView ? "#fff" : "none"} />
                </span>
                <span className="text-sm" style={{ fontWeight: favView ? 700 : 500, color: favView ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{t("market.my_favorites", "Mes favoris")}</span>
              </button>
              {user && (<button
                onClick={() => { setMineView((v) => !v); setFavView(false); setPromoView(false); }}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
                style={{ background: mineView ? "var(--yalola-primary-soft)" : "transparent" }}
                data-testid="marketplace-mine-toggle"
              >
                <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: mineView ? "var(--yalola-primary)" : "var(--yalola-surface-alt)" }}>
                  <BarChart3 size={16} color={mineView ? "#fff" : "var(--yalola-text)"} />
                </span>
                <span className="text-sm" style={{ fontWeight: mineView ? 700 : 500, color: mineView ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{t("market.mine", "Mes annonces")}</span>
              </button>)}
              {promoRules.enabled && (<button
                onClick={() => { setPromoView((v) => !v); setFavView(false); setMineView(false); }}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)]"
                style={{ background: promoView ? "var(--yalola-primary-soft)" : "transparent" }}
                data-testid="marketplace-promo-toggle"
              >
                <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: promoView ? "#D64545" : "var(--yalola-surface-alt)" }}>
                  <Percent size={16} color={promoView ? "#fff" : "var(--yalola-text)"} />
                </span>
                <span className="text-sm" style={{ fontWeight: promoView ? 700 : 500, color: promoView ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{t("market.promos", "Promos en cours")}</span>
                {promoStrip.length > 0 && <span className="ml-auto text-[11px] font-bold rounded-full px-1.5 py-0.5 text-white" style={{ background: "#D64545" }} data-testid="marketplace-promo-count">{promoStrip.length}</span>}
              </button>)}
            </div>
            <div className="yalola-card p-3 space-y-2.5" data-testid="marketplace-filters">
              <div className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("market.filters", "Filtres")}</div>
              <div>
                <Label className="text-xs">{t("market.sort_by", "Trier par")}</Label>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="mt-1 rounded-full" data-testid="marketplace-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">{t("market.sort_recent", "Plus récent")}</SelectItem>
                    <SelectItem value="price_asc">{t("market.sort_price_asc", "Prix croissant")}</SelectItem>
                    <SelectItem value="price_desc">{t("market.sort_price_desc", "Prix décroissant")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("market.price_range", "Fourchette de prix")}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" min="0" placeholder={t("market.min", "Min")} value={priceMin} onChange={(e) => setPriceMin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyPrice()} className="rounded-full" data-testid="marketplace-price-min" />
                  <span style={{ color: "var(--yalola-text-muted)" }}>–</span>
                  <Input type="number" min="0" placeholder={t("market.max", "Max")} value={priceMax} onChange={(e) => setPriceMax(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyPrice()} className="rounded-full" data-testid="marketplace-price-max" />
                </div>
              </div>
              <Button variant="outline" className="w-full rounded-full" onClick={applyPrice} data-testid="marketplace-apply-filters">{t("market.apply", "Appliquer")}</Button>
              {filtersActive && (
                <button className="text-xs w-full text-center hover:underline" style={{ color: "var(--yalola-primary)" }} onClick={clearFilters} data-testid="marketplace-clear-filters">{t("market.clear_filters", "Réinitialiser les filtres")}</button>
              )}
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0">
          {/* Mobile bar */}
          <div className="lg:hidden space-y-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
                <Input placeholder={t("market.search_ph", "Rechercher…")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full" data-testid="marketplace-search-mobile" />
              </div>
              <Button className="pill-primary rounded-full shrink-0" onClick={() => setOpen(true)} data-testid="marketplace-create-btn-mobile"><Plus size={16} /></Button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {user && (<button
                onClick={() => { setMineView((v) => !v); setFavView(false); setPromoView(false); }}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border"
                style={{ background: mineView ? "var(--yalola-primary)" : "transparent", color: mineView ? "#fff" : "var(--yalola-text)", borderColor: mineView ? "transparent" : "var(--yalola-border)" }}
                data-testid="marketplace-mine-toggle-mobile"
              >
                <BarChart3 size={12} /> {t("market.mine", "Mes annonces")}
              </button>)}
              {promoRules.enabled && (<button
                onClick={() => { setPromoView((v) => !v); setFavView(false); setMineView(false); }}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border"
                style={{ background: promoView ? "#D64545" : "transparent", color: promoView ? "#fff" : "#B42318", borderColor: promoView ? "transparent" : "#D6454580" }}
                data-testid="marketplace-promo-toggle-mobile"
              >
                <Percent size={12} /> {t("market.promos", "Promos en cours")}{promoStrip.length > 0 ? ` · ${promoStrip.length}` : ""}
              </button>)}
              {CATS.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border"
                  style={{ background: category === c.key ? "var(--yalola-primary)" : "transparent", color: category === c.key ? "#fff" : "var(--yalola-text)", borderColor: category === c.key ? "transparent" : "var(--yalola-border)" }}
                  data-testid={`marketplace-cat-mobile-${c.key}`}
                >
                  {t(c.labelKey, c.fb)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="rounded-full flex-1" data-testid="marketplace-sort-mobile"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">{t("market.sort_recent", "Plus récent")}</SelectItem>
                  <SelectItem value="price_asc">{t("market.sort_price_asc", "Prix croissant")}</SelectItem>
                  <SelectItem value="price_desc">{t("market.sort_price_desc", "Prix décroissant")}</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" min="0" placeholder={t("market.min", "Min")} value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className="rounded-full w-20" data-testid="marketplace-price-min-mobile" />
              <Input type="number" min="0" placeholder={t("market.max", "Max")} value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className="rounded-full w-20" data-testid="marketplace-price-max-mobile" />
              <Button variant="outline" className="rounded-full shrink-0 px-3" onClick={applyPrice} data-testid="marketplace-apply-filters-mobile"><Search size={15} /></Button>
            </div>
          </div>

          {mineView ? (
            <>
              {/* Lot 701 — « Mes annonces » : tableau vues / favoris / messages / story de toutes mes annonces ; un tap ouvre la fiche. */}
              <h2 className="text-lg font-bold mb-3 inline-flex items-center gap-2" style={{ fontFamily: "Outfit" }} data-testid="marketplace-mine-title"><BarChart3 size={18} style={{ color: "var(--yalola-primary)" }} /> {t("market.mine", "Mes annonces")}</h2>
              <SellerDashboard t={t} lang={lang} onOpen={(row) => { setPriceEdit(null); api.get(`/marketplace/listings/${row.id}`).then(({ data }) => setDetail(data.listing)).catch(() => toast.error(t("common.error", "Erreur"))); }} />
            </>
          ) : (<>
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>
            {favView ? t("market.my_favorites", "Mes favoris") : promoView ? t("market.promos", "Promos en cours") : (category === "all" ? t("market.recent_items", "Articles récents") : catLabel(category, t))}
            <span className="ml-2 text-sm font-normal" style={{ color: "var(--yalola-text-muted)" }}>· {shown.length}</span>
          </h2>

          {(favView ? favLoading : loading) && <CardGridSkeleton count={6} cols="grid-cols-2 md:grid-cols-3" />}
          {!(favView ? favLoading : loading) && shown.length === 0 && (
            <div className="yalola-card p-10 text-center" data-testid="marketplace-empty">
              <span className="mx-auto mb-3 h-14 w-14 rounded-full grid place-items-center" style={{ background: "#2D9E681f" }}><ShoppingBag size={26} color="#2D9E68" /></span>
              <p style={{ color: "var(--yalola-text-muted)" }}>{favView ? t("market.empty_fav", "Aucun favori pour l'instant. Touche le ♥ sur une annonce pour l'enregistrer.") : promoView ? t("market.promo_empty", "Aucune promo en cours pour l'instant. Reviens bientôt !") : t("market.empty", "Aucune annonce ici. Sois le premier à vendre un article !")}</p>
              {!favView && !promoView && <div className="mt-4 inline-block">{SellButton}</div>}
            </div>
          )}

          {/* Lot 694 — bandeau « Promos en cours » (liste générale seulement) : défilement horizontal + « Voir tout » */}
          {!favView && !promoView && category === "all" && !q && promoStrip.length > 0 && (
            <section className="mb-5" data-testid="marketplace-promo-strip">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold inline-flex items-center gap-1.5" style={{ fontFamily: "Outfit", color: "#B42318" }}><Percent size={16} /> {t("market.promos", "Promos en cours")} <span className="text-sm font-normal" style={{ color: "var(--yalola-text-muted)" }}>· {promoStrip.length}</span></h3>
                <button onClick={() => setPromoView(true)} className="text-xs font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} data-testid="marketplace-promo-see-all">{t("market.see_all", "Voir tout")}</button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {promoStrip.map((it) => <ListingCard key={it.id} it={it} mine={isMine(it)} t={t} lang={lang} compact onOpen={(x) => { setPriceEdit(null); setDetail(x); }} onToggleFav={toggleFav} />)}
              </div>
            </section>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {shown.map((it) => <ListingCard key={it.id} it={it} mine={isMine(it)} t={t} lang={lang} onOpen={(x) => { setPriceEdit(null); setDetail(x); }} onToggleFav={toggleFav} />)}
          </div>
          {!favView && hasMore && !q && (
            <div ref={sentinelRef} className="py-6 grid place-items-center" data-testid="marketplace-sentinel">
              {loadingMore && <Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} />}
            </div>
          )}
          </>)}
        </main>
      </div>

      {/* Product detail */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent variant="sheet" className="sm:max-w-2xl [&_[data-dialog-close]]:rounded-full [&_[data-dialog-close]]:bg-black/50 [&_[data-dialog-close]]:p-1.5 [&_[data-dialog-close]]:text-white [&_[data-dialog-close]]:opacity-100 [&_[data-dialog-close]]:top-[calc(var(--sa-top,0px)+0.75rem)] sm:[&_[data-dialog-close]]:top-4 sm:[&_[data-dialog-close]]:bg-transparent sm:[&_[data-dialog-close]]:p-0 sm:[&_[data-dialog-close]]:text-inherit sm:[&_[data-dialog-close]]:opacity-70" data-testid="listing-detail-dialog">
          {/* Lot 650 (PC) + lot 656 (téléphone) : la boîte ne défile plus en bloc — la galerie reste fixe (sous la barre
              système), seule la description défile, le vendeur et « Contacter le vendeur » restent visibles au-dessus
              des boutons Android. Sur téléphone la photo se réduit si l'écran est petit. */}
          <DialogBody className="p-0 sm:p-0 flex flex-col overflow-hidden">
          {detail && (
            <div className="flex flex-col flex-1 min-h-0 md:grid md:grid-cols-2 md:grid-rows-[minmax(0,1fr)]">
              <div className="bg-black flex flex-col min-h-0 shrink pt-[var(--sa-top,0px)] sm:pt-0" data-testid="listing-detail-gallery">
                {detail.images?.[0] ? (
                  // Lot 623 : plus de h-full (l'image s'étirait sur toute la cellule et la rangée de vignettes débordait sur le prix et le titre).
                  <img src={resolveMediaUrl(detail.images[Math.min(activeImg, detail.images.length - 1)])} alt="" className="block w-full min-h-0 flex-1 max-h-[48dvh] md:max-h-none object-contain" data-testid="listing-detail-image" data-index={Math.min(activeImg, detail.images.length - 1)} />
                ) : (
                  <div className="w-full aspect-square max-h-[40dvh] md:max-h-none grid place-items-center"><ShoppingBag size={48} className="opacity-30 text-white" /></div>
                )}
                {detail.images?.length > 1 && (
                  <div className="flex items-center gap-1.5 p-2 overflow-x-auto bg-black shrink-0">
                    {detail.images.map((im, i) => (
                      <button key={i} type="button" onClick={() => setActiveImg(i)} className={`relative h-14 w-14 rounded-md shrink-0 overflow-hidden ring-2 transition-opacity ${i === activeImg ? "ring-white" : "ring-transparent opacity-60 hover:opacity-100"}`} data-testid={`listing-detail-thumb-${i}`} data-active={i === activeImg ? "1" : "0"} aria-label={`${t("market.photo_n", "Photo {n}").replace("{n}", String(i + 1))}`}>
                        <img src={resolveMediaUrl(im)} alt="" className="h-full w-full object-cover" />
                        {i === 0 && <Star size={11} className="absolute top-0.5 left-0.5 text-white drop-shadow" fill="#fff" />}
                      </button>
                    ))}
                    {isMine(detail) && activeImg > 0 && (
                      <button type="button" onClick={() => saveCover(detail, activeImg)} disabled={savingCover} className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 disabled:opacity-60" data-testid="listing-detail-set-cover">
                        {savingCover ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />} {t("market.set_cover", "Photo principale")}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="p-5 pb-[calc(1.25rem+var(--sa-bottom,0px))] sm:pb-5 flex flex-col flex-1 md:min-h-0 md:overflow-hidden" data-testid="listing-detail-info">
                <div className="flex flex-col gap-1.5 text-left shrink-0">
                  <div className="flex items-center justify-between gap-2">
                    <ListingPrice it={detail} t={t} lang={lang} big />
                    <button onClick={() => toggleFav(detail)} className="h-9 w-9 grid place-items-center rounded-full shrink-0 transition-transform hover:scale-110" style={{ background: "var(--yalola-surface-alt)" }} data-testid="listing-detail-fav" title={detail.is_favorite ? t("market.fav_remove", "Retirer des favoris") : t("market.fav_add", "Ajouter aux favoris")}>
                      <Heart size={18} color={detail.is_favorite ? "#D97757" : "#6b7280"} fill={detail.is_favorite ? "#D97757" : "none"} />
                    </button>
                  </div>
                  <DialogTitle className="text-lg" style={{ fontFamily: "Outfit" }}>{detail.title}</DialogTitle>
                  <DialogDescription className="text-xs">
                    <span className="chip">{catLabel(detail.category, t)}</span>
                    {detail.city && <span className="ml-2 inline-flex items-center gap-1"><MapPin size={11} /> {detail.city}</span>}
                  </DialogDescription>
                </div>
                {detail.description && <p className="text-sm mt-3 whitespace-pre-wrap flex-1 min-h-[6rem] max-md:[contain:size] md:min-h-0 overflow-y-auto pr-1 yalola-scroll-hover" style={{ color: "var(--yalola-text)" }} data-testid="listing-detail-description">{detail.description}</p>}
                {isMine(detail) ? (
                  <div className="mt-4 pt-4 border-t space-y-2.5 shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid="listing-owner-controls">
                    {priceEdit === null ? (
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1 rounded-full" onClick={() => setPriceEdit(String(detail.price))} data-testid="listing-edit-price-btn"><Pencil size={15} className="mr-1" /> {t("market.edit_price", "Modifier le prix")}</Button>
                        <Button variant="outline" className="rounded-full text-red-600" onClick={() => removeListing(detail)} data-testid="listing-delete-btn"><Trash2 size={15} className="mr-1" /> {t("common.delete", "Supprimer")}</Button>
                      </div>
                    ) : (
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label className="text-xs">{t("market.new_price", "Nouveau prix")} ({detail.currency})</Label>
                          <Input type="number" min="0" value={priceEdit} onChange={(e) => setPriceEdit(e.target.value)} className="mt-1" data-testid="listing-price-edit-input" />
                        </div>
                        <Button className="pill-primary rounded-full" disabled={savingPrice} onClick={() => savePrice(detail)} data-testid="listing-price-save"><Check size={15} /></Button>
                        <Button variant="outline" className="rounded-full" onClick={() => setPriceEdit(null)}>{t("common.cancel", "Annuler")}</Button>
                      </div>
                    )}
                    {priceEdit === null && (
                      <Button variant="outline" className="w-full rounded-full" onClick={() => toggleSold(detail)} data-testid="listing-toggle-sold-btn">
                        {detail.active === false
                          ? <><Tag size={15} className="mr-1.5" /> {t("market.put_on_sale", "Remettre en vente")}</>
                          : <><PackageCheck size={15} className="mr-1.5" /> {t("market.mark_sold", "Marquer comme vendu")}</>}
                      </Button>
                    )}
                    {priceEdit === null && detail.active !== false && <PromoControls it={detail} t={t} onChanged={patchListing} />}
                    {priceEdit === null && detail.active !== false && detail.promo && (
                      <Button className="w-full rounded-full text-white" style={{ background: "#D64545" }} onClick={() => setShareStory(detail)} data-testid="listing-promo-share-story">
                        <PlusCircle size={15} className="mr-1.5" /> {t("market.promo_share_story", "Partager la promo en story")}
                      </Button>
                    )}
                    {priceEdit === null && <ListingStats it={detail} />}
                    <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("market.notify_hint", "Baisser le prix ou remettre en vente prévient les personnes qui ont mis cette annonce en favori 🔔")}</p>
                  </div>
                ) : detail.seller ? (
                  <div className="mt-4 pt-4 border-t space-y-2.5 shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid="listing-seller-block">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-9 w-9"><AvatarImage src={avatarSrc(detail.seller)} /><AvatarFallback>{detail.seller.name?.[0]}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1">
                        <Link to={`/profile/${detail.seller.id}`} className="text-sm font-semibold truncate hover:underline block">{detail.seller.name}</Link>
                        <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("market.seller", "Vendeur")}</div>
                      </div>
                    </div>
                    <Textarea rows={2} value={contactMsg} onChange={(e) => setContactMsg(e.target.value)} className="text-sm" data-testid="listing-contact-msg" />
                    <Button className="w-full pill-primary rounded-full" disabled={sendingMsg || !contactMsg.trim()} onClick={contactSeller} data-testid="listing-contact-btn">
                      <MessageCircle size={15} className="mr-1" /> {sendingMsg ? t("market.sending", "Envoi…") : t("market.contact_seller", "Contacter le vendeur")}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Create listing */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent variant="sheet">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{t("market.sell", "Vendre un article")}</DialogTitle>
            <DialogDescription>{t("market.sell_desc", "Ajoute des photos, un prix et une description claire pour vendre plus vite.")}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-semibold">{t("market.photos", "Photos")}</Label>
                <span className="text-xs tabular-nums" style={{ color: "var(--yalola-text-muted)" }} data-testid="listing-images-count">{form.images.length}/{maxImages}</span>
              </div>
              <div className="mt-1.5">
                <ImageCropUpload
                  aspect={1}
                  icon={ImagePlus}
                  multiple
                  keepOriginal
                  max={maxImages - form.images.length}
                  disabled={form.images.length >= maxImages}
                  onLimit={() => toast.error(t("market.photos_max", "Maximum {n} photos par annonce").replace("{n}", String(maxImages)))}
                  buttonLabel={t("market.add_photos", "Ajouter des photos")}
                  title={t("market.photo_adjust", "Ajuster la photo")}
                  onUploaded={(url) => setForm((f) => (f.images.length >= maxImages ? f : { ...f, images: [...f.images, url] }))}
                  testId="listing-image"
                />
              </div>
              {form.images.length > 0 && (
                <>
                  <Reorder.Group axis="x" values={form.images} onReorder={(v) => setForm((f) => ({ ...f, images: v }))} className="flex gap-2 overflow-x-auto pb-2 mt-2" data-testid="listing-images-reorder">
                    {form.images.map((u, i) => (
                      <Reorder.Item key={u} value={u} whileDrag={{ scale: 1.06, zIndex: 5 }} className="relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border cursor-grab active:cursor-grabbing touch-none" style={{ borderColor: "var(--yalola-border)" }} data-testid={`listing-image-item-${i}`}>
                        <img src={resolveMediaUrl(u)} alt="" className="w-full h-full object-cover pointer-events-none select-none" draggable={false} />
                        <span className="absolute top-1 left-1 h-5 min-w-[20px] px-1 grid place-items-center rounded-full bg-black/60 text-white text-[10px] font-bold">{i + 1}</span>
                        <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1 hover:bg-black/80" data-testid={`listing-image-remove-${i}`}><X size={12} /></button>
                        {i === 0 ? (
                          <span className="absolute bottom-0 inset-x-0 text-center text-[10px] font-semibold text-white bg-black/60 py-0.5" data-testid="listing-image-cover-badge">{t("market.cover_badge", "Principale")}</span>
                        ) : (
                          <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => setCover(i)} className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-0.5 text-[10px] font-semibold text-white bg-black/60 py-0.5 hover:bg-black/80" data-testid={`listing-image-set-cover-${i}`}><Star size={10} /> {t("market.set_cover_short", "Principale")}</button>
                        )}
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                  <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="listing-reorder-hint">{t("market.reorder_hint2", "Touche « Principale » (ou glisse) pour choisir la photo qui présente l'article")}</p>
                </>
              )}
            </div>
            <div><Label className="text-sm font-semibold">{t("market.title_label", "Titre *")}</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("market.title_ph", "Ex : Bible d'étude neuve")} className="mt-1.5" data-testid="listing-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">{t("market.price", "Prix")}</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" data-testid="listing-price-input" /></div>
              <div>
                <Label className="text-sm font-semibold">{t("market.currency", "Devise")}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="listing-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {localizedCurrencyLabel(c.code, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">{t("market.category", "Catégorie")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="listing-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CREATE_CATS.map((c) => <SelectItem key={c.key} value={c.key}>{t(c.labelKey, c.fb)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm font-semibold">{t("market.city", "Ville")}</Label><div className="mt-1.5"><CityAutocomplete value={form.city} country={user?.country || ""} onChange={(v) => setForm({ ...form, city: v })} testId="listing-city-input" /></div></div>
            </div>
            <div><Label className="text-sm font-semibold">{t("common.description", "Description")}</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="listing-desc-input" /></div>
            {/* Lot 700 — « Promo de lancement » (optionnelle) : l'annonce démarre avec un prix barré et un badge « -X % ». Masqué si l'admin a coupé les promos. */}
            {promoRules.enabled && (
              <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="listing-launch-promo">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" className="h-4 w-4 accent-[#D64545]" checked={launchPromo.on} onChange={(e) => setLaunchPromo((v) => ({ ...v, on: e.target.checked }))} data-testid="listing-launch-promo-toggle" />
                  <span className="text-sm font-semibold inline-flex items-center gap-1.5"><Percent size={14} style={{ color: "#D64545" }} /> {t("market.promo_launch_toggle", "Ajouter une promo de lancement")}</span>
                </label>
                {launchPromo.on ? (
                  <PromoFields basePrice={Number(form.price) || 0} currency={form.currency} price={launchPromo.price} ends={launchPromo.ends}
                    onPrice={(v) => setLaunchPromo((x) => ({ ...x, price: v }))} onEnds={(v) => setLaunchPromo((x) => ({ ...x, ends: v }))} rules={promoRules} t={t} testPrefix="listing-launch-promo" />
                ) : (
                  <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="listing-launch-promo-help">{t("market.promo_launch_help", "Ton annonce démarre avec un prix barré et un badge « -X % » ; la promo s'arrête à la date choisie. Tu pourras aussi la lancer plus tard depuis ta fiche.")}</p>
                )}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel", "Annuler")}</Button>
            <Button className="pill-primary" onClick={create} disabled={creating || !form.title.trim()} data-testid="listing-submit">{creating ? t("market.publishing", "Publication…") : t("market.publish", "Publier l'annonce")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {shareStory && <ShareToStorySheet listing={shareStory} open onOpenChange={(o) => { if (!o) setShareStory(null); }} />}
    </AppShell>
  );
};

export default Marketplace;
